import { useMutation, useQueryClient, type UseMutationResult } from '@tanstack/react-query';
import {
  updateExecutionRun,
  updateExecutionRunTags,
} from '@/features/executionRuns/data/executionRuns';
import { useOrganisationFilter } from '@/features/organisations/providers/organisationHooks';
import { patchQueryResultCaches } from '@/shared/data/queryResultCache';
import {
  gridQueryKey,
  gridPaths,
  invalidateExecutionRunSummaries,
  invalidateExecutionRuns,
  invalidateGridQuery,
  invalidateSuiteRuns,
  invalidateTestRuns,
  queryKeys,
  useInvalidateQueries,
} from '@/shared/hooks';
import type { ApiError } from '@/shared/data/client';
import type { ExecutionRunDto } from '@flowqa/shared';

type UpdateExecutionRunInput = {
  id: string;
  name?: string | null;
  source?: string | null;
  startedAt?: string | null;
  finishedAt?: string | null;
  durationMs?: number | null;
  tags?: string[];
  status?: ExecutionRunDto['status'];
};

type UpdateExecutionRunTagsInput = {
  id: string;
  tags?: string[];
};

export const useUpdateExecutionRun = (): UseMutationResult<
  ExecutionRunDto,
  ApiError,
  UpdateExecutionRunInput
> => {
  const queryClient = useQueryClient();
  const invalidateQueries = useInvalidateQueries();
  const { selectedOrganisationId } = useOrganisationFilter();
  const orgKey = selectedOrganisationId ?? null;

  return useMutation<ExecutionRunDto, ApiError, UpdateExecutionRunInput>({
    mutationFn: async (input) => await updateExecutionRun(input, selectedOrganisationId),
    onSuccess: async (updated) => {
      queryClient.setQueryData(queryKeys.executionRunDetail(updated.id, orgKey), updated);
      patchQueryResultCaches(
        queryClient,
        [
          { queryKey: queryKeys.executionRuns(orgKey, null) },
          { queryKey: queryKeys.executionRuns(orgKey, updated.projectId) },
          { queryKey: gridQueryKey(gridPaths.executionRuns) },
        ],
        updated,
      );
      await invalidateQueries([
        invalidateExecutionRuns(orgKey, undefined),
        invalidateExecutionRuns(orgKey, updated.projectId),
        invalidateSuiteRuns(updated.projectId, orgKey),
        invalidateTestRuns(updated.projectId, orgKey),
        invalidateGridQuery(gridPaths.executionRuns),
        invalidateGridQuery(gridPaths.suiteRuns),
        invalidateGridQuery(gridPaths.testRuns),
        invalidateExecutionRunSummaries(),
      ]);
    },
  });
};

export const useUpdateExecutionRunTags = (): UseMutationResult<
  ExecutionRunDto,
  ApiError,
  UpdateExecutionRunTagsInput
> => {
  const queryClient = useQueryClient();
  const invalidateQueries = useInvalidateQueries();
  const { selectedOrganisationId } = useOrganisationFilter();
  const orgKey = selectedOrganisationId ?? null;

  return useMutation<ExecutionRunDto, ApiError, UpdateExecutionRunTagsInput>({
    mutationFn: async (input) => await updateExecutionRunTags(input, selectedOrganisationId),
    onSuccess: async (updated) => {
      queryClient.setQueryData(queryKeys.executionRunDetail(updated.id, orgKey), updated);
      patchQueryResultCaches(
        queryClient,
        [
          { queryKey: queryKeys.executionRuns(orgKey, null) },
          { queryKey: queryKeys.executionRuns(orgKey, updated.projectId) },
          { queryKey: gridQueryKey(gridPaths.executionRuns) },
        ],
        updated,
      );
      await invalidateQueries([
        invalidateExecutionRuns(orgKey, undefined),
        invalidateExecutionRuns(orgKey, updated.projectId),
        invalidateSuiteRuns(updated.projectId, orgKey),
        invalidateTestRuns(updated.projectId, orgKey),
        invalidateGridQuery(gridPaths.executionRuns),
        invalidateGridQuery(gridPaths.suiteRuns),
        invalidateGridQuery(gridPaths.testRuns),
        invalidateExecutionRunSummaries(),
      ]);
    },
  });
};
